
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddHabitForm } from "@/components/habit/AddHabitForm";
import { Priority } from "@/types/habit";

const EMOJI_OPTIONS = ["🏃‍♂️", "💪", "📚", "🧘‍♀️", "🎨", "🎵", "🌱", "💻", "🏊‍♂️", "🚴‍♂️"];
const MEASUREMENT_UNITS = [
  "pages", "kilometers", "meters", "steps", "hours", "minutes", "liters", "milliliters"
];

interface AddHabitDialogProps {
  onAdd: (habit: {
    title: string;
    emoji: string;
    totalDays: number;
    reward: string;
    priority: Priority;
    dailyTarget?: {
      value: number;
      unit: string;
    };
  }) => void;
}

export const AddHabitDialog = ({ onAdd }: AddHabitDialogProps) => {
  const [title, setTitle] = useState("");
  const [selectedEmoji, setSelectedEmoji] = useState(EMOJI_OPTIONS[0]);
  const [days, setDays] = useState("30");
  const [reward, setReward] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [open, setOpen] = useState(false);
  const [dailyTargetValue, setDailyTargetValue] = useState("");
  const [dailyTargetUnit, setDailyTargetUnit] = useState(MEASUREMENT_UNITS[0]);
  const [hasDailyTarget, setHasDailyTarget] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      title,
      emoji: selectedEmoji,
      totalDays: parseInt(days),
      reward,
      priority,
      ...(hasDailyTarget && dailyTargetValue
        ? {
            dailyTarget: {
              value: parseFloat(dailyTargetValue),
              unit: dailyTargetUnit,
            },
          }
        : {}),
    });
    setTitle("");
    setSelectedEmoji(EMOJI_OPTIONS[0]);
    setDays("30");
    setReward("");
    setPriority("medium");
    setDailyTargetValue("");
    setDailyTargetUnit(MEASUREMENT_UNITS[0]);
    setHasDailyTarget(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          className={cn(
            "transition-all duration-200 rounded-full bg-primary/90 text-white px-6",
            "hover:bg-primary"
          )}
        >
          <Plus className="w-5 h-5 mr-2" />
          <span>Add New Habit</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-white/90 backdrop-blur-sm overflow-y-auto !p-0">
        <DialogHeader className="px-6 pt-4 pb-0">
          <DialogTitle className="text-black">Create a New Habit</DialogTitle>
        </DialogHeader>
        <AddHabitForm
          title={title}
          setTitle={setTitle}
          selectedEmoji={selectedEmoji}
          setSelectedEmoji={setSelectedEmoji}
          days={days}
          setDays={setDays}
          reward={reward}
          setReward={setReward}
          priority={priority}
          setPriority={setPriority}
          dailyTargetValue={dailyTargetValue}
          setDailyTargetValue={setDailyTargetValue}
          dailyTargetUnit={dailyTargetUnit}
          setDailyTargetUnit={setDailyTargetUnit}
          hasDailyTarget={hasDailyTarget}
          setHasDailyTarget={setHasDailyTarget}
          emojiOptions={EMOJI_OPTIONS}
          measurementUnits={MEASUREMENT_UNITS}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
};
